import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '@/lib/api';
import InternshipOverview from './internships/InternshipOverview';
import AllProgramsTab from './internships/AllProgramsTab';
import ProgramWorkspace from './internships/ProgramWorkspace';
import ReferralsList from './internships/ReferralsList';
import { EMPTY_PROGRAM, ProgramDialog } from './internships/shared';

const TOP_TABS = [{ id: 'overview', label: 'Overview' }, { id: 'programs', label: 'Programs' },
  { id: 'leads', label: 'Internship Leads' }];
const WORKSPACE_TABS = ['overview', 'companies', 'applications', 'placements'];

/** Program-first internship workspace: school overview, the programme list, and one
 *  workspace per programme. Selection lives in the URL so refresh and back work. */
export default function InternshipsSection({ initialTab }) {
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const programId = params.get('ip');
  const requestedWorkspaceTab = params.get('it');
  const workspaceTab = WORKSPACE_TABS.includes(requestedWorkspaceTab) ? requestedWorkspaceTab : 'overview';
  const requestedTopTab = params.get('int');
  const topTab = TOP_TABS.some(t => t.id === requestedTopTab)
    ? requestedTopTab
    : (initialTab === 'programs' ? 'programs' : 'overview');

  const load = useCallback(async () => {
    try {
      const r = await api.get('/coordinator/internship-dashboard');
      setData(r.data);
    } catch { setData(null); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const navigate = useCallback((next) => {
    setParams(prev => {
      const p = new URLSearchParams(prev);
      p.set('section', 'exp_internships');
      Object.entries(next).forEach(([k, v]) => (v ? p.set(k, v) : p.delete(k)));
      return p;
    }, { replace: false });
  }, [setParams]);

  const openProgram = (id, tab = 'overview', focus = null) => navigate({ ip: id, it: tab, int: 'programs', f: focus });
  const backToList = () => navigate({ ip: null, it: null, f: null, int: 'programs' });
  const setTopTab = (id) => navigate({ int: id, ip: null, it: null, f: null });

  const program = useMemo(
    () => (data?.programs || []).find(p => p.id === programId) || null, [data, programId]);

  if (programId) {
    return (
      <>
        <ProgramWorkspace
          program={program} loading={loading} tab={workspaceTab} focus={params.get('f')}
          setTab={(t) => navigate({ it: t, f: null })} onBack={backToList} onRefresh={load}
          onEdit={() => setEditing(program)}
        />
        {editing && <ProgramDialog program={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      </>
    );
  }

  return (
    <div data-testid="internships-section">
      <div className="flex gap-1 mb-4 bg-[#F3F4F6] rounded-lg p-1 w-fit">
        {TOP_TABS.map(t => (
          <button key={t.id} onClick={() => setTopTab(t.id)}
            className={`px-4 py-1.5 text-xs font-semibold rounded-md transition-colors ${topTab === t.id ? 'bg-white text-[#1F2937] shadow-sm' : 'text-[#6B7280] hover:text-[#1F2937]'}`}
            data-testid={`tab-int-${t.id}`}>
            {t.label}{t.id === 'leads' && data?.leads?.to_review ? ` (${data.leads.to_review})` : ''}
          </button>
        ))}
      </div>
      {topTab === 'leads' ? (
        <ReferralsList unassigned programs={data?.programs || []} onChanged={load} />
      ) : topTab === 'overview' ? (
        <InternshipOverview data={data} loading={loading} onOpenProgram={openProgram}
          onOpenPrograms={(metric) => navigate({ int: 'programs', m: metric })}
          onNewProgram={() => setEditing(EMPTY_PROGRAM)} />
      ) : (
        <AllProgramsTab data={data} loading={loading} onOpenProgram={openProgram} onRefresh={load}
          metric={params.get('m')} onClearMetric={() => navigate({ m: null })}
          editing={editing} setEditing={setEditing} />
      )}
      {topTab === 'overview' && editing && (
        <ProgramDialog program={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
      )}
    </div>
  );
}
